import { useState, useEffect, useCallback, useRef } from 'react';
import { renderRegistryBlockView } from '../components/vision-deck/blockRegistry';
import '../components/vision-deck/registryTestBlock';
import '../components/vision-deck/blocks/FrameworkMatrixBlock';
import '../components/vision-deck/blocks/whiteboard/WhiteboardBlock';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Edit3, 
  ChevronDown,
  Play, 
  Maximize2, 
  Minimize2,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { renderPremiumExtraBlock } from '../components/vision-deck/PremiumExtraBlocks';
import { normalizeExternalUrl } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Template styles with premium spacing
const TEMPLATE_STYLES = {
  'modern-executive': {
    name: 'Modern Executive',
    background: 'linear-gradient(180deg, #FFFEF5 0%, #FAF9F4 100%)',
    solidBg: '#FFFEF5',
    text: '#1A1A1A',
    accent: '#B89B5E',
    secondaryBg: '#F5F5F0',
    muted: '#6B6B6B'
  },
  'contemporary-hospitality': {
    name: 'Contemporary Hospitality',
    background: 'linear-gradient(180deg, #FDF8F3 0%, #F7F0E8 100%)',
    solidBg: '#FDF8F3',
    text: '#3D3D3D',
    accent: '#8B7355',
    secondaryBg: '#F0E6D8',
    muted: '#7D7D7D'
  },
  'corporate-prestige': {
    name: 'Corporate Prestige',
    background: 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 100%)',
    solidBg: '#1A1A1A',
    text: '#FFFFFF',
    accent: '#C9A962',
    secondaryBg: '#2D2D2D',
    muted: '#9A9A9A'
  }
};

// PAGE VISIBILITY MODES
const PAGE_MODES = {
  CONTINUOUS: 'continuous',
  HIDDEN: 'hidden',
  BUTTON_NAV: 'button_nav'
};

// Premium Block Renderer with generous spacing
function PreviewBlock({ block, style, isFullscreen, onPageNavigate, pages }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const getSpacingClasses = () => {
    switch (block.type) {
      case 'text':
        const size = block.content.size || 'text-base';
        if (size.includes('4xl') || size.includes('3xl')) return 'mb-5 md:mb-8';
        if (size.includes('2xl') || size.includes('xl')) return 'mb-4 md:mb-6';
        return 'mb-3 md:mb-5';
      case 'image':
      case 'video':
      case 'mobile-video':
        return 'mb-5 md:mb-8';
      case 'cta':
        return 'mb-4 md:mb-6 mt-3';
      case 'divider':
        return 'my-4 md:my-6';
      case 'logo':
        return 'mb-5 md:mb-8';
      default:
        return 'mb-4';
    }
  };

  const getFontSizeClass = (size) => {
    if (!isFullscreen) return size;
    const sizeMap = {
      'text-sm': 'text-base md:text-lg',
      'text-base': 'text-lg md:text-xl',
      'text-lg': 'text-xl md:text-2xl',
      'text-xl': 'text-2xl md:text-3xl',
      'text-2xl': 'text-3xl md:text-4xl',
      'text-3xl': 'text-4xl md:text-5xl',
      'text-4xl': 'text-5xl md:text-6xl lg:text-7xl',
    };
    return sizeMap[size] || size;
  };

  const handleCtaClick = (e) => {
    if (block.content.linkType === 'page' && block.content.targetPageId) {
      e.preventDefault();
      onPageNavigate?.(block.content.targetPageId);
    }
  };

  switch (block.type) {
    case 'text':
      return (
        <div className={`${getSpacingClasses()} w-full`}>
          <p 
            className={`whitespace-pre-wrap w-full ${block.content.font || 'font-sans'} ${getFontSizeClass(block.content.size || 'text-base')} ${block.content.bold ? 'font-bold' : ''} ${block.content.italic ? 'italic' : ''} ${block.content.underline ? 'underline' : ''} leading-relaxed tracking-wide`}
            style={{ 
              textAlign: block.content.align || 'left', 
              color: block.content.color || style.text
            }}
          >
            {block.content.content}
          </p>
        </div>
      );

    case 'image':
      return block.content.url ? (
        <div className={getSpacingClasses()}>
          <div 
            className={`rounded-xl md:rounded-2xl overflow-hidden ${block.content.frame?.shadow ? 'shadow-2xl' : ''} transform transition-transform duration-500 hover:scale-[1.02]`}
            style={{
              borderWidth: block.content.frame?.borderWidth || 0,
              borderColor: block.content.frame?.borderColor || '#B89B5E',
              borderStyle: 'solid',
              borderRadius: block.content.frame?.borderRadius || 16
            }}
          >
            <img src={block.content.url} alt={block.content.alt || 'Image'} className="w-full h-auto object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; if (e.target.parentNode) e.target.parentNode.style.display = 'none'; }} />
          </div>
        </div>
      ) : null;

    case 'video':
      if (isVideoPlaying || !block.content.thumbnailUrl) {
        return block.content.url ? (
          <div className={getSpacingClasses()}>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-2xl" style={{ borderRadius: block.content.frame?.borderRadius || 16 }}>
              <video src={block.content.url} controls autoPlay={isVideoPlaying} className="w-full aspect-video object-contain bg-black" poster={block.content.thumbnailUrl} />
            </div>
          </div>
        ) : null;
      }
      return (
        <div className={getSpacingClasses()}>
          <div className="relative rounded-xl md:rounded-2xl overflow-hidden cursor-pointer group shadow-2xl" style={{ borderRadius: block.content.frame?.borderRadius || 16 }} onClick={() => setIsVideoPlaying(true)}>
            <img src={block.content.thumbnailUrl} alt="Video" className="w-full aspect-video object-cover transform transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-all duration-500">
              <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-500">
                <Play className="w-10 h-10 md:w-14 md:h-14 text-[#B89B5E] ml-1" fill="currentColor" />
              </div>
            </div>
          </div>
        </div>
      );

    case 'mobile-video':
      if (isVideoPlaying || !block.content.thumbnailUrl) {
        return block.content.url ? (
          <div className={`${getSpacingClasses()} flex justify-center`}>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-2xl max-w-md w-full" style={{ borderRadius: block.content.frame?.borderRadius || 16 }}>
              <video src={block.content.url} controls autoPlay={isVideoPlaying} playsInline className="w-full aspect-[9/16] object-cover bg-black" poster={block.content.thumbnailUrl} />
            </div>
          </div>
        ) : null;
      }
      return (
        <div className={`${getSpacingClasses()} flex justify-center`}>
          <div className="relative rounded-xl md:rounded-2xl overflow-hidden cursor-pointer group shadow-2xl max-w-md w-full" style={{ borderRadius: block.content.frame?.borderRadius || 16 }} onClick={() => setIsVideoPlaying(true)}>
            <img src={block.content.thumbnailUrl} alt="Video" className="w-full aspect-[9/16] object-cover transform transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-all duration-500">
              <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-500">
                <Play className="w-10 h-10 text-[#B89B5E] ml-1" fill="currentColor" />
              </div>
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs">Mobile Optimized</div>
          </div>
        </div>
      );

    case 'logo':
      return block.content.url ? (
        <div className={getSpacingClasses()} style={{ textAlign: block.content.align || 'center' }}>
          <img 
            src={block.content.url} 
            alt="Logo" 
            className={`inline-block transform transition-all duration-500 hover:scale-105 ${
              block.content.size === 'small' ? 'h-14 md:h-16' :
              block.content.size === 'large' ? 'h-32 md:h-40' : 'h-20 md:h-28'
            }`}
            onError={(e) => { e.target.style.display = 'none'; if (e.target.parentNode) e.target.parentNode.style.display = 'none'; }}
          />
        </div>
      ) : null;

    case 'divider':
      return (
        <div className={getSpacingClasses()} style={{ textAlign: 'center' }}>
          <hr className="mx-auto opacity-60" style={{ width: block.content.width || '60%', borderTopWidth: block.content.thickness || 1, borderTopStyle: block.content.style || 'solid', borderTopColor: block.content.color || style.muted }} />
        </div>
      );

    case 'cta':
      const isPageLink = block.content.linkType === 'page';
      const targetPage = isPageLink && pages ? pages.find(p => p.id === block.content.targetPageId) : null;
      
      return (
        <div className={getSpacingClasses()} style={{ textAlign: block.content.align || 'center' }}>
          {isPageLink ? (
            <button
              onClick={handleCtaClick}
              className={`inline-block px-8 md:px-12 py-4 md:py-5 rounded-xl font-semibold text-base md:text-lg tracking-wide transition-all duration-300 transform hover:scale-105 ${
                block.content.style === 'primary' ? 'text-white shadow-xl hover:shadow-2xl' : 'border-2 hover:shadow-lg'
              }`}
              style={{
                backgroundColor: block.content.style === 'primary' ? style.accent : 'transparent',
                borderColor: style.accent,
                color: block.content.style === 'primary' ? '#FFFFFF' : style.accent
              }}
            >
              {block.content.text || targetPage?.name || 'View More'}
            </button>
          ) : (
            <a
              href={normalizeExternalUrl(block.content.url)}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-8 md:px-12 py-4 md:py-5 rounded-xl font-semibold text-base md:text-lg tracking-wide transition-all duration-300 transform hover:scale-105 ${
                block.content.style === 'primary' ? 'text-white shadow-xl hover:shadow-2xl' : 'border-2 hover:shadow-lg'
              }`}
              style={{
                backgroundColor: block.content.style === 'primary' ? style.accent : 'transparent',
                borderColor: style.accent,
                color: block.content.style === 'primary' ? '#FFFFFF' : style.accent
              }}
            >
              {block.content.text || 'Learn More'}
            </a>
          )}
        </div>
      );

    case 'testimonial':
    case 'stats':
    case 'contact-card':
    case 'comparison':
    case 'countdown':
    case 'pricing':
    case 'timeline':
    case 'faq':
    case 'roi-calculator':
    case 'booking':
    case 'lead-form':
    case 'video-testimonial':
      return renderPremiumExtraBlock(block, style);

    default:
      // Universal Builder: delegate unknown types to the additive registry.
      return renderRegistryBlockView(block, style);
  }
}

// Page Section Component for continuous scroll
function PageSection({ page, pageIndex, style, isFullscreen, onPageNavigate, pages, isDark }) {
  return (
    <section 
      id={`page-${page.id}`}
      className="flex flex-col items-stretch justify-start px-6 md:px-12 lg:px-20 py-8 md:py-10"
      style={{ background: pageIndex % 2 === 0 ? style.background : style.solidBg }}
    >
      <div className="w-full max-w-5xl mx-auto">
        {page.blocks?.length > 0 ? (
          page.blocks.map((block) => (
            <PreviewBlock 
              key={block.id} 
              block={block} 
              style={style}
              isFullscreen={isFullscreen}
              onPageNavigate={onPageNavigate}
              pages={pages}
            />
          ))
        ) : (
          <div className="flex items-center justify-center min-h-[50vh]">
            <p style={{ color: style.muted }} className="text-xl">This page is empty</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default function VisionDeckPreview() {
  const { id } = useParams();
  const { user } = useAuth();
  const [deck, setDeck] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [visiblePages, setVisiblePages] = useState(new Set());

  useEffect(() => { fetchDeck(); }, [id]);

  // Initialize visible pages based on page settings
  useEffect(() => {
    if (pages.length > 0) {
      const visible = new Set();
      pages.forEach((page, index) => {
        const mode = page.visibility || PAGE_MODES.CONTINUOUS;
        if (mode === PAGE_MODES.CONTINUOUS || index === 0) {
          visible.add(page.id);
        }
      });
      setVisiblePages(visible);
    }
  }, [pages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-hide controls
  useEffect(() => {
    let timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => { window.removeEventListener('mousemove', handleMouseMove); clearTimeout(timeout); };
  }, []);

  const fetchDeck = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to view this deck');
        return;
      }
      const response = await fetch(`${API}/api/vision-decks/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeck(data);
        setPages(data.pages?.length ? data.pages : []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load deck:', response.status, errorData);
        toast.error(errorData.detail || 'Failed to load deck');
      }
    } catch (error) {
      console.error('Error loading deck:', error);
      toast.error('Error loading deck - please check your connection');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to a specific page (for page-link buttons)
  const navigateToPage = useCallback((pageId) => {
    setVisiblePages(prev => new Set([...prev, pageId]));
    setTimeout(() => {
      const element = document.getElementById(`page-${pageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
        <div className="w-16 h-16 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Deck not found</p>
          <Button asChild>
            <Link to="/vision-decks">Back to Vision Decks</Link>
          </Button>
        </div>
      </div>
    );
  }

  const templateStyle = deck.template_id || 'modern-executive';
  const style = TEMPLATE_STYLES[templateStyle];
  const isDark = templateStyle === 'corporate-prestige';

  return (
    <div className="min-h-screen" style={{ background: style.background }}>
      {/* Preview Header with Edit Controls */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'
        }`}
        style={{ background: isDark ? 'rgba(13,13,13,0.95)' : 'rgba(255,255,255,0.95)' }}
      >
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between backdrop-blur-sm border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild className="hover:bg-black/10" style={{ color: style.text }}>
              <Link to={`/vision-decks/${id}/edit`}>
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div>
              <h1 className="font-medium" style={{ color: style.text }}>{deck.name || 'Untitled Deck'}</h1>
              <p className="text-xs" style={{ color: style.muted }}>{style.name} • Preview Mode</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleFullscreen}
              className="hover:bg-black/10"
              style={{ color: style.text }}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </Button>
            <Button variant="outline" size="sm" asChild className="border-current/30" style={{ color: style.text }}>
              <Link to={`/vision-decks/${id}/edit`}>
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Link>
            </Button>
            {deck.status === 'published' && deck.public_url && (
              <Button size="sm" asChild className="bg-[#B89B5E] hover:bg-[#A08050] text-white">
                <a href={deck.public_url} target="_blank" rel="noopener noreferrer">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </a>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Continuous Scroll Content */}
      <main>
        {pages.map((page, index) => {
          const pageMode = page.visibility || PAGE_MODES.CONTINUOUS;
          const isVisible = pageMode === PAGE_MODES.CONTINUOUS || visiblePages.has(page.id) || index === 0;
          
          if (!isVisible) return null;
          
          return (
            <PageSection 
              key={page.id}
              page={page}
              pageIndex={index}
              style={style}
              isFullscreen={isFullscreen}
              onPageNavigate={navigateToPage}
              pages={pages}
              isDark={isDark}
            />
          );
        })}
      </main>

      {/* Page Navigation Dots intentionally removed — continuous scroll mode does not need them */}

      {/* Scroll Indicator (first page only) */}
      {pages.length > 1 && (
        <div 
          className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <ChevronDown className="w-6 h-6" style={{ color: style.muted }} />
            {/* Feb 2026 — "SCROLL" label removed (was overlapping the
                chevron and bleeding through over CTA / pricing sections
                across the deck). Chevron alone communicates the hint. */}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 text-center" style={{ background: isDark ? '#0D0D0D' : style.solidBg }}>
        <p className="text-xs tracking-widest uppercase" style={{ color: style.muted }}>
          Powered by DH DESIGN
        </p>
      </footer>
    </div>
  );
}
